import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, Download, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fetchBlob = (id) => api.get(`/chat-files/file/${id}`, { responseType: "blob" }).then((r) => r.data);

const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const ImagePreview = ({ att, compact }) => {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = "";
    let alive = true;
    fetchBlob(att.file_id).then((b) => { if (alive) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => alive && setFailed(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [att.file_id]);
  if (failed) return <div className="flex items-center gap-2 text-xs opacity-80 py-2"><ImageOff className="w-4 h-4" /> تعذّر عرض الصورة</div>;
  if (!src) return <div className={`${compact ? "w-full h-20" : "w-48 h-32"} rounded-xl bg-black/10 flex items-center justify-center`}><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" data-testid={`chat-image-${att.file_id}`}>
      <img src={src} alt={`صورة مرفقة: ${att.name}`} loading="lazy" className={compact ? "w-full h-20 rounded-lg object-cover" : "max-w-[220px] max-h-56 rounded-xl object-cover"} />
    </a>
  );
};

export const ChatAttachment = ({ att, compact }) => {
  const [busy, setBusy] = useState(false);
  const isImage = att.content_type.startsWith("image/") && att.content_type !== "image/heic";
  const get = async (download) => {
    const win = download ? null : window.open("", "_blank");
    setBusy(true);
    try {
      const blob = await fetchBlob(att.file_id);
      if (win) {
        const url = URL.createObjectURL(blob);
        win.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else downloadBlob(blob, att.name);
    } catch {
      if (win) win.close();
      toast.error("تعذّر تحميل الملف");
    }
    setBusy(false);
  };
  if (isImage) return <ImagePreview att={att} compact={compact} />;
  if (compact) {
    return (
      <div className="flex flex-col items-center text-center gap-1 p-1 w-full" data-testid={`chat-file-${att.file_id}`}>
        <button type="button" onClick={() => get(false)} className="flex flex-col items-center gap-1 w-full min-w-0" title={att.name}>
          <FileText className="w-7 h-7 opacity-80" />
          <span className="text-[11px] font-semibold w-full break-all line-clamp-2" dir="auto">{att.name}</span>
        </button>
        <button type="button" onClick={() => get(true)} className="text-[10px] flex items-center gap-1 opacity-80 hover:opacity-100" data-testid={`chat-file-download-${att.file_id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {fmtSize(att.size)}
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 min-w-[200px]" data-testid={`chat-file-${att.file_id}`}>
      <FileText className="w-8 h-8 shrink-0 opacity-80" />
      <button type="button" onClick={() => get(false)} className="flex-1 text-start min-w-0">
        <div className="font-semibold truncate" dir="auto">{att.name}</div>
        <div className="text-[11px] opacity-70">{att.content_type === "image/heic" ? "صورة HEIC • " : ""}{fmtSize(att.size)}</div>
      </button>
      <button type="button" onClick={() => get(true)} className="p-1.5 rounded-full hover:bg-black/10" aria-label="تنزيل" data-testid={`chat-file-download-${att.file_id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </button>
    </div>
  );
};
